import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

// Order matters: tried strictly, first match wins. DD/MM/* is tried before
// MM/DD/* since that's the format the rest of this app writes dates in
// (see rota date pickers) — ambiguous values like "03/04/2024" resolve to
// day-first unless they only parse the other way (e.g. "13/25/2024").
const KNOWN_DATE_FORMATS = [
  "YYYY-MM-DD",
  "YYYY/MM/DD",
  "DD/MM/YYYY",
  "MM/DD/YYYY",
  "DD-MM-YYYY",
  "MM-DD-YYYY",
  "DD.MM.YYYY",
  "D/M/YYYY",
  "D-M-YYYY",
  "MMM DD, YYYY",
  "DD MMM YYYY",
  "MMMM DD, YYYY",
  "DD MMMM YYYY",
];

/**
 * Parses a date value that may arrive in any of several formats (ISO string,
 * timestamp, Date object, dayjs instance, or one of KNOWN_DATE_FORMATS) into
 * a dayjs instance. Returns null if nothing about it parses as a date.
 *
 * Use this (rather than formatDate) anywhere a dayjs object is needed, e.g.
 * feeding antd's <DatePicker value={...}> from a value of unknown shape.
 */
export const parseDate = (value) => {
  if (value === null || value === undefined || value === "") return null;

  // Date instances, numeric timestamps, and existing dayjs objects are unambiguous —
  // let dayjs handle them natively.
  if (value instanceof Date || typeof value === "number" || dayjs.isDayjs(value)) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }

  const strValue = String(value).trim();

  // ISO 8601 strings (e.g. "2024-05-03T00:00:00.000Z") are unambiguous too — parse natively
  // rather than through the strict format list below, which won't match every ISO variant.
  if (/^\d{4}-\d{2}-\d{2}/.test(strValue)) {
    const parsed = dayjs(strValue);
    if (parsed.isValid()) return parsed;
  }

  const strict = dayjs(strValue, KNOWN_DATE_FORMATS, true);
  if (strict.isValid()) return strict;

  // Last resort: dayjs' loose native parsing.
  const loose = dayjs(strValue);
  return loose.isValid() ? loose : null;
};

/**
 * Same parsing as parseDate, rendered to a display string in a single
 * consistent format. Falls back to the raw value if nothing parses, rather
 * than showing "Invalid Date".
 */
export const formatDate = (value, outputFormat = "DD/MM/YYYY") => {
  if (value === null || value === undefined || value === "") return "";

  const parsed = parseDate(value);
  return parsed ? parsed.format(outputFormat) : String(value);
};
